import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@/hooks/useUser";
import { browserAuthReturnPath } from "@/lib/safeRedirect";
import { createOtpJourneyMetadata } from "@/lib/auth/otp-journey";
import { rememberPostAuthNext } from "@/lib/post-auth-continuation";
import { rememberGuestProfileCompletion, clearGuestProfileCompletion } from "@/lib/guest-profile-completion";

async function confirmationRequest<T>(request: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([request, new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error("Email confirmation request timed out")), 30_000);
    })]);
  } finally { clearTimeout(timer); }
}

/** Uses the same protected OTP lifecycle as sign-in, without leaving the booking. */
export function GuestEmailConfirmationForm({ email }: { email: string }) {
  const { signInWithOtp, verifyEmailOtp, profile } = useUser();
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"sending" | "verifying" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const inFlight = useRef(false);
  const proof = useRef<string | undefined>(undefined);
  const next = browserAuthReturnPath(window.location, import.meta.env.BASE_URL);
  const journey = useRef(createOtpJourneyMetadata(next));
  const remaining = Math.max(0, Math.ceil((until - now) / 1000));
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!until) return;
    const timer = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= until) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [until]);
  useEffect(() => { if (sent) input.current?.focus(); }, [sent]);

  const showFailure = (err: unknown, verifying: boolean) => {
    const retryAfter = Number((err as { retryAfter?: number } | null)?.retryAfter);
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      setUntil(Date.now() + retryAfter * 1000);
      setNow(Date.now());
      setError(t("checkout.guest.code_wait", { defaultValue: "Please wait {{seconds}} seconds before trying again.", seconds: retryAfter }));
    } else {
      setError(verifying
        ? t("checkout.guest.code_invalid", { defaultValue: "That code couldn't be confirmed. Check it or request a new code." })
        : t("checkout.guest.code_send_failed", { defaultValue: "We couldn't send your code. Please try again." }));
    }
  };
  const send = async () => {
    if (inFlight.current || remaining || confirmed || !email) return;
    inFlight.current = true;
    setBusy("sending");
    setError(null);
    try {
      const result = await confirmationRequest(signInWithOtp(email, next, { force: sent, journey: journey.current }));
      if (result.error) { showFailure(result.error, false); return; }
      proof.current = result.otpJourneyProof;
      setSent(true);
      setCode("");
      setUntil(Date.now() + Math.max(60, result.retryAfterSec || 0) * 1000);
      setNow(Date.now());
    } catch (err) { showFailure(err, false); }
    finally { inFlight.current = false; setBusy(null); }
  };
  const verify = async (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current || confirmed) return;
    if (!/^\d{6}$/.test(code)) {
      setError(t("auth.otp_invalid", { defaultValue: "Enter the 6-digit code from your email." }));
      return;
    }
    inFlight.current = true;
    setBusy("verifying");
    setError(null);
    // Replace any stale modal destination before SIGNED_IN/profile hydration.
    rememberPostAuthNext(next);
    try {
      rememberGuestProfileCompletion({
        email, firstName: profile?.first_name || "", lastName: profile?.last_name || "", next,
      });
      const result = await confirmationRequest(verifyEmailOtp(email, code, { journey: journey.current, journeyProof: proof.current }));
      if (result.error) { clearGuestProfileCompletion(); showFailure(result.error, true); return; }
      setConfirmed(true);
      setCode("");
      // The shared claim coordinator binds only the genuinely verified account.
      // No booking/payment is created, resubmitted or transferred here.
    } catch (err) { showFailure(err, true); }
    finally { inFlight.current = false; setBusy(null); }
  };
  const actionClass = "min-h-11 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50";
  return <div className="mt-3">
    <p id="guest-email-code-help" className="break-words text-sm [overflow-wrap:anywhere]">
      {sent ? t("checkout.guest.code_sent_to", { defaultValue: "Code sent to {{email}}", email }) : email}
    </p>
    {confirmed ? <p role="status" className="mt-3 text-sm">
      {t("checkout.guest.email_confirmed", { defaultValue: "Email confirmed." })}
    </p> : sent ? <>
      <form onSubmit={verify} className="mt-3">
        <label htmlFor="guest-email-code" className="sr-only">
          {t("checkout.guest.code_label", { defaultValue: "Email confirmation code" })}
        </label>
        <div className="flex flex-wrap gap-2">
          <input ref={input} id="guest-email-code" name="email-confirmation-code" type="text"
            inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
            aria-describedby={`guest-email-code-help${error ? " guest-email-code-error" : ""}`}
            aria-invalid={!!error} value={code} disabled={!!busy}
            placeholder={t("auth.otp_label", { defaultValue: "6-digit code" })}
            onChange={(event) => { setCode(event.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
            className="min-h-11 min-w-0 flex-1 basis-32 rounded-xl border border-input bg-background px-3 text-base tabular-nums text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" />
          <button type="submit" disabled={!!busy || code.length !== 6} className={actionClass}>
            {busy === "verifying" ? t("auth.verifying", { defaultValue: "Verifying…" }) : t("common.confirm", { defaultValue: "Confirm" })}
          </button>
        </div>
      </form>
      <button type="button" disabled={!!busy || remaining > 0} onClick={() => void send()}
        className="mt-2 min-h-11 text-sm underline underline-offset-4 disabled:no-underline disabled:opacity-70">
        {remaining ? t("checkout.guest.code_resend_wait", { defaultValue: "Resend code in {{seconds}}s", seconds: remaining })
          : busy === "sending" ? t("auth.sending", { defaultValue: "Sending…" }) : t("auth.resend_code", { defaultValue: "Resend code" })}
      </button>
    </> : <button type="button" onClick={() => void send()} disabled={!!busy || remaining > 0 || !email}
      className={`${actionClass} mt-3 w-full`}>
      {busy === "sending" ? t("auth.sending", { defaultValue: "Sending…" })
        : remaining ? t("checkout.guest.code_resend_wait", { defaultValue: "Resend code in {{seconds}}s", seconds: remaining })
        : t("verification.send_code", { defaultValue: "Send code" })}
    </button>}
    {error && <p id="guest-email-code-error" role="alert" className="mt-2 text-sm text-destructive">{error}</p>}
  </div>;
}
